import { buildAuthHeaders } from "../forum/authHeaders";

const INSTALL_DISMISSED_KEY = "eternua.pwa.install-dismissed-at";
const INSTALL_SHOWN_KEY = "eternua.pwa.install-shown-at";
const INSTALL_VISIT_KEY = "eternua.pwa.install-visit";
const INSTALL_DISMISSAL_MS = 14 * 24 * 60 * 60 * 1000;
const INSTALL_SUGGESTION_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const PWA_CACHE_PREFIX = "eternua-pwa-";

let deferredInstallPrompt = null;
let serviceWorkerRegistration = null;
let serviceWorkerRegistrationPromise = null;
let waitingWorker = null;
let reloadForUpdate = false;
const installListeners = new Set();
const updateListeners = new Set();
const unsavedStateKeys = new Set();

function hasWindow() {
  return typeof window !== "undefined" && typeof navigator !== "undefined";
}

function safeStorage(storageName) {
  if (!hasWindow()) return null;
  try {
    return window[storageName];
  } catch (_) {
    return null;
  }
}

function emitPwaEvent(name, detail = {}) {
  if (!hasWindow() || typeof window.CustomEvent !== "function") return;
  window.dispatchEvent(new window.CustomEvent("eternua:pwa", {
    detail: { name, ...detail },
  }));
}

export function recordPwaLifecycleEvent(name) {
  const allowed = new Set(["install_suggestion_shown", "standalone_launch", "update_failed"]);
  if (allowed.has(name)) emitPwaEvent(name);
}

function publishInstallState() {
  const state = getPwaCapabilities();
  installListeners.forEach((listener) => listener(Boolean(deferredInstallPrompt), state));
}

function publishUpdateState(overrides = {}) {
  const state = {
    available: Boolean(waitingWorker),
    version: null,
    error: null,
    ...overrides,
  };
  updateListeners.forEach((listener) => listener(state));
}

function isStandalone() {
  if (!hasWindow()) return false;
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.matchMedia?.("(display-mode: fullscreen)")?.matches
    || navigator.standalone === true
  );
}

function isIos() {
  if (!hasWindow()) return false;
  const platform = String(navigator.platform || "");
  const userAgent = String(navigator.userAgent || "");
  return /iPad|iPhone|iPod/.test(userAgent)
    || (platform === "MacIntel" && Number(navigator.maxTouchPoints) > 1);
}

function isIosSafari() {
  if (!isIos()) return false;
  const userAgent = String(navigator.userAgent || "");
  return /Safari/i.test(userAgent) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(userAgent);
}

function installDismissalIsActive() {
  const dismissedAt = Number(safeStorage("localStorage")?.getItem(INSTALL_DISMISSED_KEY) || 0);
  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < INSTALL_DISMISSAL_MS;
}

function installSuggestionWasRecentlyShown() {
  const shownAt = Number(safeStorage("localStorage")?.getItem(INSTALL_SHOWN_KEY) || 0);
  return Number.isFinite(shownAt) && Date.now() - shownAt < INSTALL_SUGGESTION_COOLDOWN_MS;
}

export function getPwaCapabilities() {
  const supported = hasWindow() && "serviceWorker" in navigator;
  return {
    supported,
    standalone: isStandalone(),
    installPromptAvailable: Boolean(deferredInstallPrompt),
    installSuggestionAllowed: !installDismissalIsActive() && !installSuggestionWasRecentlyShown(),
    ios: isIos(),
    iosSafari: isIosSafari(),
    notificationsSupported: hasWindow() && "Notification" in window,
    notificationPermission: hasWindow() && "Notification" in window
      ? Notification.permission
      : "unsupported",
    pushSupported: hasWindow() && "PushManager" in window && supported,
    online: hasWindow() ? navigator.onLine !== false : true,
  };
}

export function markPwaInstallVisit() {
  const storage = safeStorage("sessionStorage");
  const previous = storage?.getItem(INSTALL_VISIT_KEY) === "seen";
  storage?.setItem(INSTALL_VISIT_KEY, "seen");
  return previous;
}

export function dismissInstallSuggestion() {
  safeStorage("localStorage")?.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
  publishInstallState();
  emitPwaEvent("install_suggestion_dismissed");
}

export function markInstallSuggestionShown() {
  safeStorage("localStorage")?.setItem(INSTALL_SHOWN_KEY, String(Date.now()));
  emitPwaEvent("install_suggestion_shown");
}

export function subscribeInstallAvailability(listener) {
  installListeners.add(listener);
  listener(Boolean(deferredInstallPrompt), getPwaCapabilities());
  return () => installListeners.delete(listener);
}

export function subscribePwaUpdate(listener) {
  updateListeners.add(listener);
  listener({ available: Boolean(waitingWorker), version: null, error: null });
  return () => updateListeners.delete(listener);
}

function watchRegistration(registration) {
  serviceWorkerRegistration = registration;
  if (registration.waiting) {
    waitingWorker = registration.waiting;
    publishUpdateState();
  }
  registration.addEventListener("updatefound", () => {
    const candidate = registration.installing;
    if (!candidate) return;
    candidate.addEventListener("statechange", () => {
      if (candidate.state === "installed" && navigator.serviceWorker.controller) {
        waitingWorker = registration.waiting || candidate;
        publishUpdateState();
        emitPwaEvent("update_available");
      }
    });
  });
  return registration;
}

function registerNow() {
  if (!hasWindow() || !("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") {
    return Promise.resolve(null);
  }
  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register("/service-worker.js", { scope: "/", updateViaCache: "none" })
      .then(watchRegistration)
      .catch((error) => {
        publishUpdateState({ error: "Le mode hors ligne est indisponible. Vous pouvez continuer à utiliser le site en ligne." });
        emitPwaEvent("service_worker_error", { phase: "registration" });
        throw error;
      });
  }
  return serviceWorkerRegistrationPromise;
}

export function registerEternuaServiceWorker() {
  if (hasWindow() && "serviceWorker" in navigator && process.env.NODE_ENV === "development") {
    // A previous production preview can leave a worker controlling localhost.
    // Release its cache without touching accounts or browser-local journals.
    return navigator.serviceWorker.getRegistrations().then(async (registrations) => {
      await Promise.all(registrations.map((registration) => registration.unregister()));
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(keys.filter((key) => key.startsWith(PWA_CACHE_PREFIX)).map((key) => caches.delete(key)));
      }
      return null;
    }).catch(() => null);
  }
  if (!hasWindow() || !("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") {
    return Promise.resolve(null);
  }
  if (document.readyState === "complete") return registerNow();
  return new Promise((resolve) => {
    window.addEventListener("load", () => {
      registerNow().then(resolve).catch(() => resolve(null));
    }, { once: true });
  });
}

async function ensureServiceWorkerRegistration() {
  if (!hasWindow() || !("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return watchRegistration(existing);
  const registration = await navigator.serviceWorker.register(
    "/service-worker.js",
    { scope: "/", updateViaCache: "none" }
  );
  return watchRegistration(registration);
}

if (hasWindow()) {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    publishInstallState();
    emitPwaEvent("install_prompt_available");
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    safeStorage("localStorage")?.removeItem(INSTALL_DISMISSED_KEY);
    safeStorage("localStorage")?.removeItem(INSTALL_SHOWN_KEY);
    publishInstallState();
    emitPwaEvent("installed");
  });
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      waitingWorker = null;
      publishUpdateState({ available: false });
      if (reloadForUpdate) {
        reloadForUpdate = false;
        window.location.reload();
      }
    });
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "PWA_ACTIVATED") {
        publishUpdateState({ available: false, version: event.data.version || null });
      }
    });
  }
}

export async function installEternuaApp() {
  if (!deferredInstallPrompt) return { available: false, accepted: false };
  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  publishInstallState();
  await prompt.prompt();
  const choice = await prompt.userChoice;
  const accepted = choice?.outcome === "accepted";
  emitPwaEvent(accepted ? "install_accepted" : "install_declined");
  if (!accepted) dismissInstallSuggestion();
  return { available: true, accepted };
}

export function setPwaUnsavedState(key, active) {
  const safeKey = String(key || "unknown");
  if (active) unsavedStateKeys.add(safeKey);
  else unsavedStateKeys.delete(safeKey);
}

export function hasPwaUnsavedChanges() {
  return unsavedStateKeys.size > 0;
}

export async function applyPwaUpdate() {
  if (hasPwaUnsavedChanges()) {
    emitPwaEvent("update_blocked_unsaved");
    return { applied: false, blocked: true, reason: "unsaved-changes" };
  }
  const registration = serviceWorkerRegistration
    || (hasWindow() && "serviceWorker" in navigator
      ? await navigator.serviceWorker.getRegistration("/")
      : null);
  const worker = registration?.waiting || waitingWorker;
  if (!worker) return { applied: false, blocked: false, reason: "not-available" };
  reloadForUpdate = true;
  worker.postMessage({ type: "SKIP_WAITING" });
  emitPwaEvent("update_accepted");
  return { applied: true, blocked: false };
}

export function dismissPwaUpdate() {
  publishUpdateState({ available: false });
  emitPwaEvent("update_deferred");
}

function applicationServerKey(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

function pushHeaders(extra = {}) {
  return buildAuthHeaders({ Accept: "application/json", ...extra });
}

async function parseApiError(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  return String(payload?.detail || payload?.error || fallback);
}

export async function getPushStatus() {
  if (!hasWindow()) return { configured: false, subscribed: false };
  const response = await fetch("/api/notifications/push/status/", {
    headers: pushHeaders(),
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(await parseApiError(response, "Statut push indisponible."));
  return response.json();
}

function defaultDeviceLabel() {
  if (!hasWindow()) return "Navigateur";
  const userAgent = String(navigator.userAgent || "");
  const browser = /Edg/i.test(userAgent)
    ? "Edge"
    : /Firefox|FxiOS/i.test(userAgent)
      ? "Firefox"
      : /Chrome|CriOS/i.test(userAgent)
        ? "Chrome"
        : /Safari/i.test(userAgent)
          ? "Safari"
          : "Navigateur";
  const device = isIos() ? (/iPad/i.test(userAgent) ? "iPad" : "iPhone") : "cet appareil";
  return `${browser} sur ${device}`.slice(0, 80);
}

async function subscribeServerPush(registration) {
  const status = await getPushStatus();
  if (!status.configured || !status.public_key || !("PushManager" in window)) {
    return { serverConfigured: false, subscribed: false };
  }
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(status.public_key),
    });
  }
  const payload = {
    ...subscription.toJSON(),
    device_label: defaultDeviceLabel(),
  };
  const response = await fetch("/api/notifications/push/subscription/", {
    method: "POST",
    headers: pushHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await parseApiError(response, "L’abonnement push n’a pas pu être enregistré."));
  }
  emitPwaEvent("push_enabled");
  return { serverConfigured: true, subscribed: true };
}

export async function enableLocalNotifications() {
  if (!hasWindow() || !("Notification" in window) || !("serviceWorker" in navigator)) {
    return { supported: false, permission: "unsupported" };
  }
  const permission = Notification.permission === "default"
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== "granted") {
    emitPwaEvent("push_permission_not_granted", { permission });
    return { supported: true, permission };
  }
  const registration = await ensureServiceWorkerRegistration();
  if (!registration) return { supported: false, permission };
  const server = await subscribeServerPush(registration);
  await registration.showNotification("Notifications Eternua activées", {
    body: "Vous pourrez recevoir vos rappels et nouvelles activités.",
    icon: "/icons/icon-192.png",
    badge: "/icons/notification-badge-96.png",
    tag: "eternua-notifications-enabled",
    data: { url: "/notifications" },
  });
  return { supported: true, permission, ...server };
}

export async function disableServerPushForCurrentDevice() {
  if (!hasWindow() || !("serviceWorker" in navigator)) return { supported: false };
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return { supported: true, disabled: false };
  const endpoint = String(subscription.endpoint || "");
  try {
    await fetch("/api/notifications/push/subscription/", {
      method: "DELETE",
      headers: pushHeaders({ "Content-Type": "application/json" }),
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({ endpoint }),
    });
  } finally {
    await subscription.unsubscribe().catch(() => false);
  }
  emitPwaEvent("push_disabled_current_device");
  return { supported: true, disabled: true };
}

export async function listPushDevices() {
  const response = await fetch("/api/notifications/push/devices/", {
    headers: pushHeaders(),
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(await parseApiError(response, "Appareils indisponibles."));
  const payload = await response.json();
  return Array.isArray(payload?.results) ? payload.results : [];
}

export async function revokePushDevice(publicId) {
  const safeId = encodeURIComponent(String(publicId || ""));
  const response = await fetch(`/api/notifications/push/devices/${safeId}/`, {
    method: "DELETE",
    headers: pushHeaders(),
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(await parseApiError(response, "Cet appareil n’a pas pu être révoqué."));
  emitPwaEvent("push_device_revoked");
  return true;
}

export async function sendTestPush() {
  const response = await fetch("/api/notifications/push/test/", {
    method: "POST",
    headers: pushHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    cache: "no-store",
    body: "{}",
  });
  if (!response.ok) throw new Error(await parseApiError(response, "La notification de test n’a pas pu être envoyée."));
  emitPwaEvent("push_test_sent");
  return response.json().catch(() => ({}));
}

function postToServiceWorkers(message) {
  if (!hasWindow() || !("serviceWorker" in navigator)) return Promise.resolve();
  return navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      [registration.active, registration.waiting, registration.installing]
        .filter(Boolean)
        .forEach((worker) => worker.postMessage(message));
    });
  });
}

export async function clearPrivatePwaData() {
  unsavedStateKeys.clear();
  if (hasWindow()) {
    const storage = safeStorage("localStorage");
    if (storage) {
      const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(Boolean);
      keys.filter((key) => key.startsWith("eternua.agenda.offline.v1.")).forEach((key) => storage.removeItem(key));
    }
  }
  await postToServiceWorkers({ type: "CLEAR_PRIVATE_DATA" });
}

export async function clearLocalPwaData() {
  await postToServiceWorkers({ type: "CLEAR_LOCAL_DATA" });
  if (hasWindow() && "caches" in window) {
    const names = await window.caches.keys();
    await Promise.all(names.filter((name) => (
      name.startsWith(PWA_CACHE_PREFIX) && !name.startsWith(`${PWA_CACHE_PREFIX}shell-`)
    )).map((name) => window.caches.delete(name)));
  }
  safeStorage("localStorage")?.removeItem(INSTALL_DISMISSED_KEY);
  safeStorage("localStorage")?.removeItem(INSTALL_SHOWN_KEY);
  safeStorage("sessionStorage")?.removeItem(INSTALL_VISIT_KEY);
  emitPwaEvent("local_data_cleared");
}

export async function getPwaStorageEstimate() {
  if (!hasWindow() || !navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return {
    usage: Number(estimate.usage || 0),
    quota: Number(estimate.quota || 0),
  };
}

export async function getPwaVersion() {
  if (!hasWindow() || !("serviceWorker" in navigator) || typeof MessageChannel === "undefined") return null;
  const registration = serviceWorkerRegistration || await navigator.serviceWorker.getRegistration("/");
  const worker = registration?.active || navigator.serviceWorker.controller;
  if (!worker) return null;
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(() => resolve(null), 1500);
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      resolve(event.data?.version || null);
    };
    worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
  });
}
